/**
 * PeriodTimeSlider — Dual-handle range slider for escalation periods.
 *
 * Range: minTime–maxTime in seconds (typically 0–3600 or 0–alert_ttl).
 * Displays time in minutes.
 *
 * Props:
 *   start    — start time in seconds
 *   end      — end time in seconds
 *   setStart — setter for start value
 *   setEnd   — setter for end value
 *   minTime  — minimum value in seconds (default 0)
 *   maxTime  — maximum value in seconds (default 3600)
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const SNAP = 60; // 1-minute increments

const PeriodTimeSlider = ({ start = 0, end = 3600, setStart, setEnd, minTime = 0, maxTime = 3600 }) => {
    const [dragging, setDragging] = useState(null);
    const trackRef = useRef(null);

    const range = maxTime - minTime;
    const snap = (val) => Math.round(val / SNAP) * SNAP;

    const getTimeFromEvent = useCallback((e) => {
        if (!trackRef.current) return minTime;
        const rect = trackRef.current.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const raw = minTime + Math.round(pct * range);
        return Math.max(minTime, Math.min(maxTime, snap(raw)));
    }, [minTime, maxTime, range]);

    // Clamp values when min/max change
    useEffect(() => {
        let newStart = start;
        let newEnd = end;
        let changed = false;

        if (newStart < minTime) { newStart = minTime; changed = true; }
        if (newEnd > maxTime) { newEnd = maxTime; changed = true; }
        if (newStart >= newEnd) {
            newStart = minTime;
            newEnd = Math.min(maxTime, minTime + 3600);
            changed = true;
        }

        if (changed) {
            setStart(newStart);
            setEnd(newEnd);
        }
    }, [start, end, minTime, maxTime, setStart, setEnd]);

    const handleMouseDown = (handle, e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(handle);
    };

    useEffect(() => {
        if (!dragging) return;

        const onMove = (e) => {
            const time = getTimeFromEvent(e);
            if (dragging === 'start') {
                setStart(Math.min(time, end - SNAP));
            } else {
                setEnd(Math.max(time, start + SNAP));
            }
        };

        const onUp = () => setDragging(null);

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [dragging, start, end, setStart, setEnd, getTimeFromEvent]);

    const startPct = range > 0 ? ((start - minTime) / range) * 100 : 0;
    const endPct = range > 0 ? ((end - minTime) / range) * 100 : 100;

    const toMin = (s) => Math.floor(s / 60);

    // Generate tick marks at 25% intervals
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((p) => minTime + Math.round(p * range));

    return (
        <div className="cf-slider">
            <div className="cf-slider__labels">
                <span>{toMin(start)} min</span>
                <span>{toMin(end)} min</span>
            </div>
            <div className="cf-slider__track" ref={trackRef}>
                <div
                    className="cf-slider__fill"
                    style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
                />
                <div
                    className="cf-slider__handle"
                    style={{ left: `${startPct}%` }}
                    onMouseDown={(e) => handleMouseDown('start', e)}
                    role="slider"
                    aria-label="Period start"
                    aria-valuenow={start}
                    aria-valuemin={minTime}
                    aria-valuemax={maxTime}
                    tabIndex={0}
                />
                <div
                    className="cf-slider__handle"
                    style={{ left: `${endPct}%` }}
                    onMouseDown={(e) => handleMouseDown('end', e)}
                    role="slider"
                    aria-label="Period end"
                    aria-valuenow={end}
                    aria-valuemin={minTime}
                    aria-valuemax={maxTime}
                    tabIndex={0}
                />
            </div>
            <div className="cf-slider__ticks">
                {ticks.map((t) => (
                    <span key={t}>{toMin(t)} min</span>
                ))}
            </div>
        </div>
    );
};

export default PeriodTimeSlider;
